import { z } from 'zod';
import { emailField, phoneField, linkFields } from './links';

export const GenderSchema = z.enum(['female', 'male', 'other']);
export type Gender = z.infer<typeof GenderSchema>;

const name = z.string().trim().max(80);
const place = z.string().trim().max(120);

export const PersonFieldsSchema = z
  .object({
    firstName: name.nullish(),
    lastName: name.nullish(),
    birthName: name.nullish(),
    gender: GenderSchema.nullish(),
    born: place.nullish(),
    birthPlace: place.nullish(),
    living: z.boolean().default(true),
    died: place.nullish(),
    restingPlace: place.nullish(),
    occupation: place.nullish(),
    residence: place.nullish(),
    story: z.string().max(20000).nullish(),
    email: emailField,
    phone: phoneField,
    ...linkFields,
    photoId: z.string().nullish(),
  })
  .refine((p) => Boolean(p.firstName?.trim()) || Boolean(p.lastName?.trim()), {
    message: 'A person needs a first or last name',
  });
export type PersonFields = z.infer<typeof PersonFieldsSchema>;

export const PersonSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  birthName: z.string().nullable(),
  gender: GenderSchema.nullable(),
  born: z.string().nullable(),
  bornYear: z.number().int().nullable(),
  birthPlace: z.string().nullable(),
  living: z.boolean(),
  died: z.string().nullable(),
  diedYear: z.number().int().nullable(),
  restingPlace: z.string().nullable(),
  occupation: z.string().nullable(),
  residence: z.string().nullable(),
  story: z.string().nullable(),
  email: z.string().nullable().default(null),
  phone: z.string().nullable().default(null),
  instagram: z.string().nullable().default(null),
  facebook: z.string().nullable().default(null),
  linkedin: z.string().nullable().default(null),
  twitter: z.string().nullable().default(null),
  website: z.string().nullable().default(null),
  photoId: z.string().nullable(),
  createdAt: z.number(),
});
export type Person = z.infer<typeof PersonSchema>;

export const ParentLinkSchema = z.object({
  parentId: z.string(),
  childId: z.string(),
  kind: z.enum(['birth', 'adoptive']).default('birth'),
});
export type ParentLink = z.infer<typeof ParentLinkSchema>;

export const PartnershipSchema = z.object({
  id: z.string(),
  aId: z.string(),
  bId: z.string(),
  status: z.enum(['married', 'partner', 'divorced', 'widowed']).default('married'),
});
export type Partnership = z.infer<typeof PartnershipSchema>;

export const PhotoRefSchema = z.object({
  id: z.string(),
  width: z.number().nullable(),
  height: z.number().nullable(),
});
export type PhotoRef = z.infer<typeof PhotoRefSchema>;

export const RoleSchema = z.enum(['owner', 'editor', 'viewer']);
export type Role = z.infer<typeof RoleSchema>;

export const TreeSnapshotSchema = z.object({
  tree: z.object({ id: z.string(), name: z.string(), ownerId: z.string() }),
  me: z.object({
    role: RoleSchema,
    personId: z.string().nullable(),
    perspectivePersonId: z.string().nullable(),
    /** Cards this account added. Editors may change only these; owners may change any. */
    ownPersonIds: z.array(z.string()).default([]),
  }),
  people: z.array(PersonSchema),
  parentLinks: z.array(ParentLinkSchema),
  partnerships: z.array(PartnershipSchema),
  photos: z.array(PhotoRefSchema),
  /** Newest change to each card: who made it (display name) and when. Keyed by person id. */
  lastEdits: z.record(z.string(), z.object({ by: z.string().nullable(), at: z.number() })).default({}),
  version: z.number().int(),
});
export type TreeSnapshot = z.infer<typeof TreeSnapshotSchema>;

export const RelationSchema = z.object({
  anchorId: z.string(),
  type: z.enum(['parent', 'partner', 'child', 'sibling']),
  isStepParent: z.boolean().default(false),
  otherParentId: z.string().nullish(),
  linkAsPartnerOfExistingParent: z.boolean().default(false),
});
export type Relation = z.infer<typeof RelationSchema>;

export const CreatePersonBodySchema = z.object({
  person: PersonFieldsSchema,
  relation: RelationSchema.nullish(),
  photoId: z.string().nullish(),
});
export type CreatePersonBody = z.infer<typeof CreatePersonBodySchema>;

export const ERROR_CODES = [
  'too_many_parents',
  'cycle',
  'needs_parent',
  'invite_email_mismatch',
  'not_found',
  'forbidden',
  'unauthorized',
  'invalid',
  'rate_limited',
  'conflict',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

/** Parse a free-text date such as "1952" or "14 Mar 1952" into a year. */
export function parseYear(text: string | null | undefined): number | null {
  if (!text) return null;
  const m = /(?<!\d)(\d{4})(?!\d)/.exec(text);
  if (!m) return null;
  const y = Number(m[1]);
  return y >= 1000 && y <= 2999 ? y : null;
}

export function displayName(p: Pick<Person, 'firstName' | 'lastName'>): string {
  return [p.firstName, p.lastName].filter(Boolean).join(' ').trim() || 'Unnamed';
}

/** Born and died years: "b. 1952", "1921 – 2004", "d. 1990". Spanish uses "n." (nacido) and "f." (fallecido). */
export function lifespan(p: Pick<Person, 'bornYear' | 'diedYear' | 'living'>, locale: 'en' | 'es' = 'en'): string {
  const [born, died] = locale === 'es' ? ['n.', 'f.'] : ['b.', 'd.'];
  const b = p.bornYear;
  if (p.living) return b ? `${born} ${b}` : '';
  if (b && p.diedYear) return `${b} – ${p.diedYear}`;
  if (b) return `${b} – ?`;
  if (p.diedYear) return `${died} ${p.diedYear}`;
  return '';
}
